import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sticky warning shown to any user whose admin has set rename_warning_at on their profile.
 * Stays open on every page until the user updates their real name (saving auto-clears the flag).
 * Forced flow — no bypass options.
 */
export default function RenameWarningModal() {
  const { user, updateUser } = useAuth();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const flagged = !!user?.rename_warning_at;

  useEffect(() => {
    if (flagged) setDraft(user?.name || "");
  }, [flagged, user?.name]);

  if (!flagged) return null;

  const save = async () => {
    const next = draft.trim();
    if (next.length < 2) { toast.error("Name too short (min 2 characters)"); return; }
    if (next === user.name) { toast.error("Please enter your REAL name (not the same one)"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/me/name", { name: next });
      updateUser(data);
      toast.success("Real name saved. Account verified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[110] grid place-items-center p-4 modal-backdrop"
        data-testid="rename-warning-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-lg bg-surface border-2 border-red-500/70 rounded-3xl overflow-hidden shadow-[0_24px_80px_-12px_rgba(185,28,28,0.6)]"
        >
          {/* Header — solid red so text reads white */}
          <div className="relative px-7 pt-8 pb-6 text-center bg-red-800">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-950 border-2 border-red-300 grid place-items-center mb-4 shadow-[0_0_24px_rgba(248,113,113,0.55)]">
              <AlertTriangle className="text-white" size={28}/>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-950 border border-red-300 text-white text-[10px] tracking-[0.3em] uppercase font-bold mb-3">
              Action Required
            </div>
            <div className="font-display text-3xl uppercase tracking-tight leading-tight text-white">
              Update Your <span className="text-red-100 underline decoration-red-300 decoration-2 underline-offset-4">Real Name</span>
            </div>
          </div>

          <div className="p-6 sm:p-7 space-y-5">
            <div className="p-4 rounded-2xl bg-red-900 border border-red-500/60 text-sm leading-relaxed text-white">
              In order to access your account, please update the <span className="font-bold underline decoration-red-300 decoration-2 underline-offset-2">real name</span> on your profile so we can match it against the Momentum employee list and verify your account.
            </div>

            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2 block">Your full real name</label>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={60}
                placeholder="e.g. Abdul Basit Khan"
                data-testid="rename-warning-input"
                className="w-full bg-black/40 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple focus:ring-1 focus:ring-purple outline-none"
              />
              <p className="text-[10px] text-white/50 mt-2">Currently shown as: <span className="text-white/80 font-semibold">{user.name}</span> · League: @{user.league_name}</p>
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="rename-warning-save"
              className="btn-bounce cta-sweep w-full px-5 py-3 rounded-full bg-gradient-to-r from-vivid to-purple hover:from-purple hover:to-lavender hover:text-plum text-white font-bold uppercase tracking-wider text-sm transition-all shadow-[0_4px_18px_rgba(160,64,192,0.45)] inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Pencil size={14}/> {saving ? "Saving…" : "Save Real Name"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
